import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Plus,
  Trash2,
  ArrowUp,
  ArrowDown,
  X,
  Sliders,
  Settings2,
} from "lucide-react";
import {
  useDealCustomFields,
  type DealCustomField,
  type CustomFieldType,
  CUSTOM_FIELD_TYPE_LABELS,
} from "./useDealCustomFields";
import { useConfigurationContext } from "../root/ConfigurationContext";
import { usePipelines } from "./usePipelines";

// -------------------------------------------------------------
// 1. DIALOG: ADICIONAR NOVO CAMPO
// -------------------------------------------------------------
export const AddCustomFieldDialog = ({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const { addField } = useDealCustomFields();
  const { dealStages } = useConfigurationContext();
  const { pipelines } = usePipelines();

  const [name, setName] = useState("");
  const [type, setType] = useState<CustomFieldType>("text");
  const [options, setOptions] = useState<string[]>([]);
  const [newOptionInput, setNewOptionInput] = useState("");
  const [showOnCreate, setShowOnCreate] = useState(false);
  const [isRequired, setIsRequired] = useState(false);
  const [requiredOnCreate, setRequiredOnCreate] = useState(false);
  const [selectedStages, setSelectedStages] = useState<string[]>([]);
  const [selectedPipelines, setSelectedPipelines] = useState<string[]>([]);

  useEffect(() => {
    if (open) {
      setName("");
      setType("text");
      setOptions([]);
      setNewOptionInput("");
      setShowOnCreate(false);
      setIsRequired(false);
      setRequiredOnCreate(false);
      setSelectedStages([]);
      setSelectedPipelines([]);
    }
  }, [open]);

  const handleAddOption = () => {
    const trimmed = newOptionInput.trim();
    if (trimmed && !options.includes(trimmed)) {
      setOptions([...options, trimmed]);
      setNewOptionInput("");
    }
  };

  const handleRemoveOption = (index: number) => {
    setOptions(options.filter((_, idx) => idx !== index));
  };

  const toggleStage = (stageVal: string) => {
    setSelectedStages((prev) =>
      prev.includes(stageVal)
        ? prev.filter((s) => s !== stageVal)
        : [...prev, stageVal],
    );
  };

  const togglePipeline = (pipeId: string) => {
    setSelectedPipelines((prev) =>
      prev.includes(pipeId)
        ? prev.filter((p) => p !== pipeId)
        : [...prev, pipeId],
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    addField({
      name: name.trim(),
      type,
      options:
        type === "select" || type === "multiselect" ? options : undefined,
      showOnCreate,
      requiredRule: isRequired
        ? {
            requiredOnCreate,
            requiredStages:
              selectedStages.length > 0 ? selectedStages : undefined,
            requiredPipelines:
              selectedPipelines.length > 0 ? selectedPipelines : undefined,
          }
        : undefined,
    });

    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit} className="space-y-4">
          <DialogHeader>
            <DialogTitle className="text-base font-semibold flex items-center gap-2">
              <Plus className="w-4 h-4 text-primary" />
              Adicionar Novo Campo nos Detalhes
            </DialogTitle>
          </DialogHeader>

          {/* Nome do Campo */}
          <div className="space-y-1.5">
            <Label htmlFor="cf-name" className="text-xs">
              Nome do Campo *
            </Label>
            <Input
              id="cf-name"
              placeholder="Ex: Porte do Projeto, Número do Contrato..."
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="h-8 text-xs"
              required
              autoFocus
            />
          </div>

          {/* Tipo do Campo */}
          <div className="space-y-1.5">
            <Label className="text-xs">Tipo de Campo</Label>
            <Select
              value={type}
              onValueChange={(val) => setType(val as CustomFieldType)}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(
                  Object.keys(CUSTOM_FIELD_TYPE_LABELS) as CustomFieldType[]
                ).map((key) => (
                  <SelectItem key={key} value={key} className="text-xs">
                    {CUSTOM_FIELD_TYPE_LABELS[key]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Se for Opção única ou Múltipla escolha, adicionar opções */}
          {(type === "select" || type === "multiselect") && (
            <div className="space-y-2 p-3 bg-muted/40 rounded-lg border border-border/60">
              <Label className="text-xs font-semibold">Opções de Seleção</Label>
              <div className="flex gap-1.5">
                <Input
                  placeholder="Nova opção..."
                  value={newOptionInput}
                  onChange={(e) => setNewOptionInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddOption();
                    }
                  }}
                  className="h-7 text-xs flex-1"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleAddOption}
                  className="h-7 text-xs"
                >
                  Adicionar
                </Button>
              </div>

              {options.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {options.map((opt, idx) => (
                    <span
                      key={idx}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-card border border-border text-xs"
                    >
                      {opt}
                      <button
                        type="button"
                        onClick={() => handleRemoveOption(idx)}
                        className="hover:text-destructive text-muted-foreground"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Caixa de Seleção: Ao criar novo negócio */}
          <div className="flex items-center space-x-2 pt-2 border-t border-border/40">
            <Checkbox
              id="cf-create"
              checked={showOnCreate}
              onCheckedChange={(checked) => setShowOnCreate(Boolean(checked))}
            />
            <Label
              htmlFor="cf-create"
              className="text-xs font-medium cursor-pointer"
            >
              Ao criar novo negócio (exibir este campo no formulário de criação)
            </Label>
          </div>

          {/* Opção Obrigatório */}
          <div className="space-y-3 pt-2 border-t border-border/40">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="cf-required"
                checked={isRequired}
                onCheckedChange={(checked) => setIsRequired(Boolean(checked))}
              />
              <Label
                htmlFor="cf-required"
                className="text-xs font-semibold cursor-pointer text-foreground"
              >
                Definir como campo obrigatório
              </Label>
            </div>

            {/* Sub-opções de obrigatoriedade */}
            {isRequired && (
              <div className="pl-6 space-y-3 bg-muted/30 p-3 rounded-lg border border-border/50">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="cf-req-create"
                    checked={requiredOnCreate}
                    onCheckedChange={(c) => setRequiredOnCreate(Boolean(c))}
                  />
                  <Label
                    htmlFor="cf-req-create"
                    className="text-xs cursor-pointer"
                  >
                    Obrigatório ao criar novo negócio
                  </Label>
                </div>

                {/* Obrigatório para avançar em etapas específicas */}
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium">
                    Obrigatório para entrar nas seguintes etapas:
                  </Label>
                  <p className="text-[11px] text-muted-foreground">
                    Se o negócio for movido para esta etapa sem o campo
                    preenchido, uma janela abrirá solicitando o preenchimento.
                  </p>
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    {dealStages.map((stg) => {
                      const isSelected = selectedStages.includes(stg.value);
                      return (
                        <button
                          key={stg.value}
                          type="button"
                          onClick={() => toggleStage(stg.value)}
                          className={`px-2 py-1 rounded-md text-[11px] border transition-colors cursor-pointer ${
                            isSelected
                              ? "bg-primary text-primary-foreground border-primary font-medium"
                              : "bg-card text-muted-foreground hover:bg-muted border-border"
                          }`}
                        >
                          {stg.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Obrigatório para funis específicos */}
                {pipelines.length > 0 && (
                  <div className="space-y-1.5 pt-2 border-t border-border/40">
                    <Label className="text-xs font-medium">
                      Obrigatório para os seguintes funis:
                    </Label>
                    <div className="flex flex-wrap gap-1.5 pt-1">
                      {pipelines.map((pipe) => {
                        const isSelected = selectedPipelines.includes(pipe.id);
                        return (
                          <button
                            key={pipe.id}
                            type="button"
                            onClick={() => togglePipeline(pipe.id)}
                            className={`px-2 py-1 rounded-md text-[11px] border transition-colors cursor-pointer ${
                              isSelected
                                ? "bg-primary text-primary-foreground border-primary font-medium"
                                : "bg-card text-muted-foreground hover:bg-muted border-border"
                            }`}
                          >
                            {pipe.nome}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter className="pt-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onOpenChange(false)}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              size="sm"
              className="bg-primary text-primary-foreground"
            >
              Salvar Campo
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// -------------------------------------------------------------
// 2. DIALOG: EDITAR CAMPOS
// -------------------------------------------------------------
export const EditCustomFieldsDialog = ({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const { fields, updateField, deleteField } = useDealCustomFields();
  const [editingFieldId, setEditingFieldId] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [showOnCreate, setShowOnCreate] = useState(false);
  const [isRequired, setIsRequired] = useState(false);
  const [requiredOnCreate, setRequiredOnCreate] = useState(false);

  const startEdit = (field: DealCustomField) => {
    setEditingFieldId(field.id);
    setName(field.name);
    setShowOnCreate(field.showOnCreate);
    setIsRequired(Boolean(field.requiredRule));
    setRequiredOnCreate(Boolean(field.requiredRule?.requiredOnCreate));
  };

  const handleSaveEdit = () => {
    if (!editingFieldId || !name.trim()) return;

    updateField(editingFieldId, {
      name: name.trim(),
      showOnCreate,
      requiredRule: isRequired
        ? {
            requiredOnCreate,
          }
        : undefined,
    });
    setEditingFieldId(null);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-base font-semibold flex items-center gap-2">
            <Settings2 className="w-4 h-4 text-primary" />
            Editar Campos dos Detalhes
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 py-2">
          {fields.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-4">
              Nenhum campo personalizado cadastrado.
            </p>
          ) : (
            fields.map((f) => (
              <div
                key={f.id}
                className="p-3 rounded-lg border border-border/70 bg-card/60 space-y-2"
              >
                {editingFieldId === f.id ? (
                  <div className="space-y-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Nome do Campo</Label>
                      <Input
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="h-8 text-xs"
                      />
                    </div>

                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id={`edit-create-${f.id}`}
                        checked={showOnCreate}
                        onCheckedChange={(c) => setShowOnCreate(Boolean(c))}
                      />
                      <Label
                        htmlFor={`edit-create-${f.id}`}
                        className="text-xs cursor-pointer"
                      >
                        Ao criar novo negócio
                      </Label>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id={`edit-req-${f.id}`}
                        checked={isRequired}
                        onCheckedChange={(c) => setIsRequired(Boolean(c))}
                      />
                      <Label
                        htmlFor={`edit-req-${f.id}`}
                        className="text-xs cursor-pointer"
                      >
                        Obrigatório
                      </Label>
                    </div>

                    <div className="flex items-center justify-end gap-2 pt-2 border-t border-border/40">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setEditingFieldId(null)}
                        className="h-7 text-xs"
                      >
                        Cancelar
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        onClick={handleSaveEdit}
                        className="h-7 text-xs bg-primary text-primary-foreground"
                      >
                        Salvar
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-xs text-foreground truncate">
                          {f.name}
                        </span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground border border-border/50">
                          {CUSTOM_FIELD_TYPE_LABELS[f.type]}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mt-1 text-[11px] text-muted-foreground">
                        {f.showOnCreate && (
                          <span className="text-blue-500">• Na criação</span>
                        )}
                        {f.requiredRule && (
                          <span className="text-amber-500">• Obrigatório</span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-1.5 shrink-0">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => startEdit(f)}
                        className="h-7 px-2 text-xs"
                      >
                        Editar
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => deleteField(f.id)}
                        className="h-7 px-2 text-xs text-destructive hover:bg-destructive/10"
                        title="Excluir campo"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(false)}
          >
            Fechar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// -------------------------------------------------------------
// 3. DIALOG: REORDENAR CAMPOS
// -------------------------------------------------------------
export const ReorderCustomFieldsDialog = ({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const { fields, reorderFields } = useDealCustomFields();
  const [localList, setLocalList] = useState<DealCustomField[]>([]);

  useEffect(() => {
    if (open) {
      setLocalList([...fields]);
    }
  }, [open, fields]);

  const moveUp = (index: number) => {
    if (index === 0) return;
    const next = [...localList];
    const temp = next[index - 1];
    next[index - 1] = next[index];
    next[index] = temp;
    setLocalList(next);
  };

  const moveDown = (index: number) => {
    if (index === localList.length - 1) return;
    const next = [...localList];
    const temp = next[index + 1];
    next[index + 1] = next[index];
    next[index] = temp;
    setLocalList(next);
  };

  const handleSaveOrder = () => {
    reorderFields(localList);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-base font-semibold flex items-center gap-2">
            <Sliders className="w-4 h-4 text-primary" />
            Reordenar Posição dos Detalhes
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-1.5 py-3">
          {localList.map((f, index) => (
            <div
              key={f.id}
              className="flex items-center justify-between p-2 rounded-lg border border-border/70 bg-card/60 text-xs"
            >
              <div className="flex items-center gap-2 min-w-0">
                <span className="font-mono text-muted-foreground w-4 text-center">
                  {index + 1}.
                </span>
                <span className="font-medium truncate">{f.name}</span>
                <span className="text-[10px] text-muted-foreground">
                  ({CUSTOM_FIELD_TYPE_LABELS[f.type]})
                </span>
              </div>

              <div className="flex items-center gap-1 shrink-0">
                <button
                  type="button"
                  disabled={index === 0}
                  onClick={() => moveUp(index)}
                  className="p-1 rounded hover:bg-muted disabled:opacity-30 cursor-pointer"
                  title="Mover para cima"
                >
                  <ArrowUp className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  disabled={index === localList.length - 1}
                  onClick={() => moveDown(index)}
                  className="p-1 rounded hover:bg-muted disabled:opacity-30 cursor-pointer"
                  title="Mover para baixo"
                >
                  <ArrowDown className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(false)}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={handleSaveOrder}
            className="bg-primary text-primary-foreground"
          >
            Salvar Ordem
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
